import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SidebarInset, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Separator } from '@radix-ui/react-separator';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ModeToggle } from '../ModeToggle';
import { ThemeSelector } from '../ThemeSelector';
import { useMemo, type MouseEvent } from 'react';
import { getHasPermissionRoutes, routes } from '@/router/routes';
import { buildRouteConfigMap, getBreadcrumbItems } from '@/router/utils';
import { useUserStore } from '@/stores/userStore';

export const MainContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { userInfo } = useUserStore();

  const authorizedRoutes = useMemo(() => {
    const permissionCodes = userInfo?.permissionCodes || [];
    if (!permissionCodes.length) return [];
    return getHasPermissionRoutes(permissionCodes, routes[0].children || [], (route) => !!route.meta);
  }, [userInfo?.permissionCodes]);

  const routeMap = useMemo(() => buildRouteConfigMap(authorizedRoutes), [authorizedRoutes]);

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItems(location.pathname, authorizedRoutes, routeMap),
    [authorizedRoutes, location.pathname, routeMap]
  );

  // 处理面包屑点击
  const handleBreadcrumbClick = (href: string, e: MouseEvent) => {
    e.preventDefault();
    navigate(href);
  };
  return (
    <SidebarInset>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4!" />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbItems.map((item, index) => (
              <div key={item.href} className="flex items-center">
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {index === breadcrumbItems.length - 1 ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      href={item.href}
                      onClick={(e) => handleBreadcrumbClick(item.href, e)}
                    >
                      {item.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </div>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="ml-auto flex items-center gap-2">
          <ThemeSelector />
          <ModeToggle />
        </div>
      </header>
      <main
        className={cn(
          'space-y-4 p-4 overflow-auto relative',
          /** 限制高度为 100vh-顶部导航栏高度-(默认外边距 * 2) */
          'h-[calc(100vh-(var(--spacing)*12)-(var(--spacing)*3*2))]',
          {
            /** 限制宽度为100vw-侧边栏宽度-SidebarInset设置的右边距 */
            'w-[calc(100vw-var(--sidebar-width)-(var(--spacing)*3*1))]': !isMobile,
            /** isMobile模式下，SidebarInset不止设置了右边距，还有左边距*/
            'w-[calc(100vw-(var(--spacing)*3*2))]': isMobile,
          },
        )}
      >
        <Outlet />
      </main>
    </SidebarInset>
  );
};
